import type { LiveBreakWidgetCopy } from "@loot-os/api-contract";
import closeIcon from "@loot-os/icons/icons/close";
import infoIcon from "@loot-os/icons/icons/info";
import twitchIcon from "@loot-os/icons/icons/twitch";
import {
  type CSSProperties,
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Avatar } from "../../core/Avatar.js";
import { Button } from "../../core/Button.js";
import { buttonStyles } from "../../core/Button.styles.js";
import { Icon } from "../../core/Icon.js";
import { IconButton } from "../../core/IconButton.js";
import { Popover } from "../../core/Popover.js";
import { Text } from "../../core/Text.js";
import { Tooltip } from "../../core/Tooltip.js";
import { AppLink } from "../../foundations/AppLink.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import { widgetSurface } from "./liveBreakWidget.js";
import {
  type LiveBreakPipLayout,
  PIP_CORNERS,
  PIP_SCALED_PLAYER_WIDTH_PX,
  type PipCorner,
  playerScale,
} from "./pipGestures.js";
import { usePipFrame } from "./usePipFrame.js";

/**
 * What the frame's header offers about signing in: Log in to the store; Sign in to Twitch (the
 * link's popup, whose twitch.tv sign-in signs the player in too); or Connected, with a way to sign
 * the player in again. Where the browser keeps the player signed out whatever happens (Safari,
 * iOS), linking still works but can't sign it in, and the header says to use another browser.
 */
export type LiveBreakPipAccount =
  | { status: "signedOut"; onLogIn: () => void }
  | { status: "notLinked"; onConnect: () => void; playerStaysSignedOut?: boolean }
  | { status: "linked"; onRefresh: () => void; playerStaysSignedOut?: boolean };

export interface LiveBreakPipFrameProps {
  /** Whose stream plays: the stream row's name and Twitch avatar. */
  name: string;
  avatarSrc?: string | null;
  /**
   * The live break it streams, while that isn't the page under it: the name and avatar link
   * there, and Join leads the header's actions. Null or left out (a promoted stream, which has
   * none, or the live break's own page), neither.
   */
  liveBreakHref?: string | null;
  words: LiveBreakWidgetCopy;
  /** The header's sign-in action; null or left out while it loads, or where Twitch is hidden. */
  account?: LiveBreakPipAccount | null;
  /** ✕: closes the frame (the app keeps the pin, so the stream row comes back). */
  onClose: () => void;
  /** Where it sits and how wide, which the app keeps per tab; every drag, resize and pinch reports the next. */
  layout: LiveBreakPipLayout;
  onLayoutChange: (layout: LiveBreakPipLayout) => void;
  /**
   * The Twitch player: the app's `<iframe title=…>` on
   * `player.twitch.tv/?channel=…&parent={location.hostname}`. It fills a 16:9 box, laid out at
   * least 533⅓ × 300 (Twitch's minimum) and scaled down to fit a narrower frame. Keep the element
   * mounted while the stream plays: a new one restarts it.
   */
  children: ReactNode;
  /**
   * `dock`: in the widget's corner column, dragged, resized and pinched within the viewport.
   * `inline`: where the flow puts it, at its layout's width, for a preview of the store.
   */
  placement?: "dock" | "inline";
  /** The Connected explainer open from the start (a story's). */
  defaultExplainerOpen?: boolean;
  className?: string;
}

const CORNER_POSITION: Record<PipCorner, string> = {
  tl: "-top-3 -left-3",
  tr: "-top-3 -right-3",
  bl: "-bottom-3 -left-3",
  br: "-bottom-3 -right-3",
};

/** Whose stream it is: the avatar, the name with its live dot, and the line under it. */
function PipIdentity({
  name,
  avatarSrc,
  words,
  linked = false,
}: {
  name: string;
  avatarSrc?: string | null;
  words: LiveBreakWidgetCopy;
  linked?: boolean;
}) {
  return (
    <>
      <Avatar src={avatarSrc} name={name} size="sm" decorative outlined />
      <span className={cn("flex min-w-0 flex-col", !linked && "flex-1")}>
        <span className="flex min-w-0 items-center gap-1.5">
          <Text
            as="span"
            variant="body-b2-strong"
            tone="primary"
            className={cn("truncate", linked && "group-hover/pipname:underline")}
          >
            {name}
          </Text>
          <span aria-hidden className="size-2 shrink-0 rounded-pill bg-status-danger" />
          <span className="sr-only">{words.live}</span>
        </span>
        <Text as="span" variant="body-b3" tone="secondary">
          {words.pip.viewing}
        </Text>
      </span>
    </>
  );
}

/** The 16:9 box the player fills: under the scaled width, the player is laid out wider and scaled. */
function PipPlayer({ children }: { children: ReactNode }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    setBoxWidth(box.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    // The entry's box is the layout width, which a pinch's scale on the frame leaves alone.
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBoxWidth(entry.contentRect.width);
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const scale = playerScale(boxWidth);
  const scaled: CSSProperties | undefined =
    scale === null ? undefined : { width: PIP_SCALED_PLAYER_WIDTH_PX, scale: String(scale) };
  return (
    <div
      ref={boxRef}
      className="relative w-full overflow-hidden bg-bg-inset"
      style={scale === null ? undefined : { height: (boxWidth * 9) / 16 }}
    >
      <div
        data-pip-player
        data-scaled={scale === null ? undefined : ""}
        className={cn(
          "aspect-video [&_iframe]:block [&_iframe]:size-full [&_iframe]:border-0",
          scale === null ? "w-full" : "absolute top-0 left-0 origin-top-left",
        )}
        style={scaled}
      >
        {children}
      </div>
    </div>
  );
}

/** A thumb's size on a phone: the header's buttons are 40 px below md, 32 px from it. */
const PHONE_TARGET = "max-md:h-control-md";

/** The header's sign-in action, by the account's state. */
function PipAccountAction({
  account,
  words,
  defaultExplainerOpen,
}: {
  account: LiveBreakPipAccount;
  words: LiveBreakWidgetCopy["pip"];
  defaultExplainerOpen?: boolean;
}) {
  const hintId = useId();
  const [open, setOpen] = useState(defaultExplainerOpen ?? false);
  if (account.status === "signedOut") {
    return (
      <Button size="sm" onClick={account.onLogIn} className={PHONE_TARGET}>
        {words.logIn}
      </Button>
    );
  }
  if (account.status === "notLinked") {
    if (!account.playerStaysSignedOut) {
      return (
        <Button size="sm" icon={twitchIcon} onClick={account.onConnect} className={PHONE_TARGET}>
          {words.signInToTwitch}
        </Button>
      );
    }
    return (
      <>
        <Tooltip content={words.otherBrowser}>
          <Button
            size="sm"
            icon={twitchIcon}
            onClick={account.onConnect}
            aria-describedby={hintId}
            className={PHONE_TARGET}
          >
            {words.connectTwitch}
          </Button>
        </Tooltip>
        <span id={hintId} className="sr-only">
          {words.otherBrowser}
        </span>
      </>
    );
  }
  const { onRefresh, playerStaysSignedOut } = account;
  return (
    <Popover
      title={words.connectedTitle}
      side="top"
      align="end"
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          className={cn(
            "group flex cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-bg-hover",
            FOCUS_RING,
          )}
        >
          <Icon
            icon={infoIcon}
            size={14}
            className="text-text-secondary group-hover:text-text-primary"
          />
          <Text as="span" variant="body-b3-strong" tone="success">
            {words.connected}
          </Text>
          <span className="sr-only">{words.connectedHint}</span>
        </button>
      }
    >
      <p>
        {words.connectedBody}{" "}
        {playerStaysSignedOut ? (
          words.reconnectOtherBrowser
        ) : (
          <>
            {words.adsQuestion}{" "}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onRefresh();
              }}
              className={cn(
                "cursor-pointer rounded-sm text-accent-text underline underline-offset-2",
                FOCUS_RING,
              )}
            >
              {words.refresh}
            </button>
          </>
        )}
      </p>
    </Popover>
  );
}

/**
 * Picture-in-picture (boxed's `PipViewer`): the Twitch player in a frame the shopper drags, resizes
 * from its corners and, on a touch screen, pinches, headed by whose stream it is (a link to its
 * live break, with Join, while that isn't the page under it), a way to sign in to Twitch, and ✕.
 * It moves by transforms alone and scales the player past Twitch's 400×300 minimum to shrink to a
 * phone's width; under reduced motion it appears without its entrance.
 */
export function LiveBreakPipFrame({
  name,
  avatarSrc,
  liveBreakHref,
  words,
  account,
  onClose,
  layout,
  onLayoutChange,
  children,
  placement = "dock",
  defaultExplainerOpen,
  className,
}: LiveBreakPipFrameProps) {
  const theme = useLayout();
  const movable = placement === "dock";
  const frame = usePipFrame({ layout, onLayoutChange, enabled: movable });
  const { isInteracting, isCoarsePointer, pinchScale } = frame;
  const style: CSSProperties = {
    ...(movable ? { translate: `${layout.offset.x}px ${layout.offset.y}px` } : {}),
    ...(layout.width === null ? {} : { width: layout.width }),
    ...(pinchScale === 1 ? {} : { scale: String(pinchScale) }),
  };
  return (
    <section
      ref={frame.containerRef}
      aria-label={words.pip.label(name)}
      onPointerDown={frame.onSurfacePointerDown}
      style={style}
      className={cn(
        "relative w-full max-w-full origin-bottom-left",
        isInteracting
          ? "[&_iframe]:pointer-events-none"
          : "transition-[scale,opacity] duration-base ease-enter starting:scale-105 starting:opacity-0 motion-reduce:transition-none",
        movable && isCoarsePointer && "touch-none",
        className,
      )}
    >
      <div
        className={cn(
          "flex w-full flex-col overflow-hidden rounded-xl",
          widgetSurface(theme.chrome),
        )}
      >
        <div
          className={cn(
            "@container/pip flex min-h-11 touch-none items-center gap-2 border-b border-border-subtle py-1 pr-2 pl-3 select-none",
            movable && !isCoarsePointer && (isInteracting ? "cursor-grabbing" : "cursor-grab"),
          )}
        >
          {liveBreakHref ? (
            // The link hugs the name, so the header's free space beside it still drags the frame.
            <span className="flex min-w-0 flex-1 items-center">
              {/* Named by what it shows (the host, Live, Viewing): a label that left the
                  visible words out would not match what a voice-control user says. */}
              <AppLink
                href={liveBreakHref}
                className={cn(
                  "group/pipname -my-1 -ml-1 flex min-w-0 items-center gap-2 rounded-md py-1 pr-1 pl-1",
                  FOCUS_RING,
                )}
              >
                <PipIdentity name={name} avatarSrc={avatarSrc} words={words} linked />
              </AppLink>
            </span>
          ) : (
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <PipIdentity name={name} avatarSrc={avatarSrc} words={words} />
            </span>
          )}
          {liveBreakHref && (
            <AppLink
              href={liveBreakHref}
              aria-label={words.joinLabel(name)}
              className={buttonStyles({ variant: "primary", size: "sm", className: PHONE_TARGET })}
            >
              {words.join}
            </AppLink>
          )}
          {account && (
            // A phone-narrow frame drops the action's icon, so the stream's name keeps its room.
            <span className="flex shrink-0 items-center @max-[22rem]/pip:[&_button>svg:first-child]:hidden">
              <PipAccountAction
                account={account}
                words={words.pip}
                defaultExplainerOpen={defaultExplainerOpen}
              />
            </span>
          )}
          <IconButton
            icon={closeIcon}
            label={words.pip.close}
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="max-md:size-control-md"
          />
        </div>
        <PipPlayer>{children}</PipPlayer>
      </div>
      {movable &&
        PIP_CORNERS.map((corner) => {
          const handle = frame.resizeHandles[corner];
          return (
            <div
              key={corner}
              aria-hidden
              data-pip-resize-handle={corner}
              onPointerDown={handle.onPointerDown}
              style={{ cursor: handle.cursor }}
              className={cn(
                "absolute z-10 size-6 touch-none",
                CORNER_POSITION[corner],
                !handle.isActive && "pointer-events-none",
              )}
            />
          );
        })}
    </section>
  );
}
